/**
 * Cache key factories shared across every query/mutation hook.
 *
 * Kept in their own module so domain-specific hook files can import
 * from ``./keys`` without dragging in the full barrel — and so the
 * key shapes live in exactly one place if the cache layout shifts.
 */

import type { AdminOverviewParams } from "@/api/types";

import type { AdminAuditParams, AdminPlatformAuditParams } from "./admin/audit";
import type { AdminInvoiceListParams } from "./admin/invoices";
import type { AdminOrderListParams } from "./admin/orders";

// --- Customer-facing query keys --------------------------------------

export const queryKeys = {
  catalog: (lang: string) => ["catalog", lang] as const,
  quote: (slug: string) => ["quote", slug] as const,
  cart: () => ["cart"] as const,
  /** KAN-1876 — poslední „ceník se od přidání změnil" z úpravy řádku.
   * Schválně MIMO ``["cart"]``: invalidace košíku ho nesmí smazat. */
  cartPriceListChange: () => ["cart-price-list-change"] as const,
  cartAddons: () => ["cart", "addons"] as const,
  cartCheckoutPreview: (
    countryCode: string | undefined,
    carrierCode: string | undefined,
    ladderId: number | null,
    deliveryMethod: "carrier" | "pickup" = "carrier",
    vatId: string | undefined = undefined,
  ) =>
    [
      "cart",
      "checkoutPreview",
      countryCode,
      carrierCode,
      ladderId,
      deliveryMethod,
      vatId,
    ] as const,
  order: (orderNumber: string) => ["order", orderNumber] as const,
  orderShipments: (orderNumber: string) =>
    ["order", orderNumber, "shipments"] as const,
  orderPaymentInstructions: (orderNumber: string) =>
    ["order", orderNumber, "payment-instructions"] as const,
  businessInfo: () => ["business-info"] as const,
  quoteValidity: () => ["quote-validity"] as const,
  /** KAN-1673 — the signed-in customer's price quotes (My projects). */
  myQuotes: () => ["my-quotes"] as const,
  adminBusinessInfo: () => ["admin", "business-info"] as const,
  // Per-tenant override of the platform business-info — same shape,
  // edited by tenant admins, drives their own subdomain footer.
  adminTenantBusinessInfo: () => ["admin", "tenant-business-info"] as const,
  orgDomainStatus: (slug: string) => ["orgs", slug, "domain-status"] as const,
} as const;

// --- Admin query keys -------------------------------------------------

export const adminKeys = {
  me: () => ["auth", "me"] as const,
  sessions: () => ["auth", "sessions"] as const,
  overview: (params: AdminOverviewParams) =>
    ["admin", "overview", params] as const,
  orders: (params: AdminOrderListParams) =>
    ["admin", "orders", params] as const,
  orderStatusTransitions: () =>
    ["admin", "orders", "status-transitions"] as const,
  // Sales invoices / proformas / credit notes (admin „Faktury" surface).
  invoices: (params: AdminInvoiceListParams) =>
    ["admin", "invoices", params] as const,
  invoice: (id: number) => ["admin", "invoices", id] as const,
  // Split/merge deliveries: coverage + shipments for an order, and the
  // candidate orders that could be consolidated with it.
  orderShipments: (orderNumber: string) =>
    ["admin", "orders", orderNumber, "shipments"] as const,
  mergeableOrders: (orderNumber: string) =>
    ["admin", "orders", orderNumber, "mergeable-orders"] as const,
  // KAN-304 — strom kontejnerů jedné zakázky (editor sestavy).
  assemblyTree: (orderNumber: string) =>
    ["admin", "orders", orderNumber, "assembly-tree"] as const,
  auditLog: (params: AdminAuditParams) =>
    ["admin", "audit-log", params] as const,
  platformAuditLog: (params: AdminPlatformAuditParams) =>
    ["admin", "platform-audit-log", params] as const,
  platformAuditFacets: () => ["admin", "platform-audit-log", "facets"] as const,
  // PR-60: per-tenant branding overrides for the active ProviderOrg.
  branding: () => ["admin", "branding"] as const,
  // Embed funnel counts, per window length — a 7-day read must not
  // serve a 30-day one out of cache.
  embedFunnel: (days: number) => ["admin", "embed-funnel", days] as const,
  // Per-tenant email provider (custom from_email + DKIM/SPF DNS instrukce).
  emailProvider: () => ["admin", "email-provider"] as const,
  // PR-61: org info (plan tier + entitlements) for the active ProviderOrg.
  org: (slug: string) => ["admin", "org", slug] as const,
  // PR-61: tenant rule list.
  rules: () => ["admin", "rules"] as const,
  // Catalog of variables admins may use in a rule condition (served by
  // the BE so the FE builder doesn't hardcode the set).
  ruleVariables: () => ["admin", "rules", "variables"] as const,
  // Catalog of action types admins may use in a rule action (served by
  // the BE so the FE builder doesn't hardcode the set).
  ruleActionTypes: () => ["admin", "rules", "action-types"] as const,
  // PR-61: customer-org list under the active ProviderOrg.
  customerOrgs: () => ["admin", "customer-orgs"] as const,
  // PR-61: pending approvals queue for CustomerOrg admins.
  approvals: () => ["approvals"] as const,
  // Read-only queue for the provider admin (GET /admin/approvals).
  adminApprovals: () => ["admin", "approvals"] as const,
  // PR-62: per-tenant webhook subscriptions + delivery history.
  webhooks: () => ["admin", "webhooks"] as const,
  webhookDeliveries: (subId: number) =>
    ["admin", "webhooks", subId, "deliveries"] as const,
  // PR-62: per-tenant integration toggle list.
  integrations: () => ["admin", "integrations"] as const,
  // KAN-385: sjednocený seznam rozšíření (appky + konektory) s jednou
  // osou stavu. Vlastní klíč, ne pod ``["admin","apps"]`` — instalace
  // appky i přepnutí konektoru ho musí zneplatnit, takže ho invalidují
  // obě mutační cesty explicitně.
  extensions: () => ["admin", "extensions"] as const,
  featureSettings: () => ["admin", "feature-settings"] as const,
  // PR-67: per-tenant carbon dashboard summary.
  carbonSummary: (from: string | null, to: string | null) =>
    ["admin", "carbon", "summary", from ?? "", to ?? ""] as const,
  // Per-tenant CarbonFactor CRUD (lf70 migration).
  carbonFactors: () => ["admin", "carbon", "factors"] as const,
  // PR-88: enterprise auth surfaces — SAML config (per-org IdP
  // metadata), permission catalog + caller's effective grants.
  samlConfig: () => ["admin", "saml-config"] as const,
  // KAN-167: OIDC config — sibling surface to samlConfig.
  oidcConfig: () => ["admin", "oidc-config"] as const,
  // KAN-169: the SSO-required switch. Deliberately NOT under either
  // protocol's key — both setup screens read the same policy, and two
  // keys would let one screen show a stale switch after the other
  // flipped it.
  ssoPolicy: () => ["admin", "sso-policy"] as const,
  // KAN-168: mapování IdP → role/skupiny; společné pro OIDC i SAML.
  ssoMapping: () => ["admin", "sso-mapping"] as const,
  permissionsMe: (slug: string) =>
    ["admin", "permissions", slug, "me"] as const,
  permissionsCatalog: (slug: string) =>
    ["admin", "permissions", slug, "catalog"] as const,
  // PR-93: Stripe Connect KYC + billing surfaces.
  contracts: () => ["admin", "billing", "contracts"] as const,
  kycStatus: () => ["admin", "billing", "kyc"] as const,
  disputes: () => ["admin", "billing", "disputes"] as const,
  // PR-31: Stripe Customer Portal redirect + invoice/upcoming surfaces.
  upcomingInvoice: () => ["admin", "billing", "upcoming"] as const,
  invoiceHistory: () => ["admin", "billing", "invoices"] as const,
  planHistory: () => ["admin", "billing", "plan-history"] as const,
  planLimits: () => ["admin", "plan-limits"] as const,
  planFeatures: () => ["admin", "plan-features"] as const,
  // Per-tenant public-FE customization (page visibility + i18n overrides).
  pagesVisibility: () => ["admin", "pages-visibility"] as const,
  siteAppearance: () => ["admin", "site-appearance"] as const,
  i18nOverrides: () => ["admin", "i18n-overrides"] as const,
  // Platform-shell i18n overrides — sibling of ``i18nOverrides`` but
  // singleton-keyed and gated on ``RequirePlatformBrandingPage`` /
  // ``RequirePlatformConfigWrite`` rather than tenant admin role.
  platformI18nOverrides: () => ["admin", "platform", "i18n-overrides"] as const,
  platformLandingDemo: () => ["admin", "platform", "landing-demo"] as const,
  // DB-backed language registry (Jazyky a překlady page). CRUD over the
  // ``/:lang`` set that used to be the hardcoded ``SUPPORTED_LANGS``.
  platformLanguages: () => ["admin", "platform", "languages"] as const,
  // DB-backed country catalog (KAN-1449) — replaces the hardcoded list.
  platformCountries: () => ["admin", "platform", "countries"] as const,
  // KAN-1643 — public holidays per country (same page as the catalog).
  platformCountryHolidays: (code: string, year: number) =>
    ["admin", "platform", "countries", "holidays", code, year] as const,
  platformHolidayCoverage: () =>
    ["admin", "platform", "countries", "holiday-coverage"] as const,
  // DB-backed currency catalog (KAN-1451) — replaces the hardcoded tuple.
  platformCurrencies: () => ["admin", "platform", "currencies"] as const,
  // KAN-1568: interní obchodní CRM platformy.
  crm: () => ["admin", "platform", "crm"] as const,
  crmCompanies: (q: string) => ["admin", "platform", "crm", "companies", q] as const,
  crmCompany: (id: number) => ["admin", "platform", "crm", "company", id] as const,
  crmToday: (today: string) => ["admin", "platform", "crm", "today", today] as const,
  crmMatch: (addresses: string) => ["admin", "platform", "crm", "match", addresses] as const,
  crmMap: () => ["admin", "platform", "crm", "map"] as const,
  crmVariants: () => ["admin", "platform", "crm", "variants"] as const,
  crmDraftLearning: () => ["admin", "platform", "crm", "learning", "drafts"] as const,
  crmReasonTags: () => ["admin", "platform", "crm", "reason-tags"] as const,
  crmBlockers: () => ["admin", "platform", "crm", "blockers"] as const,
  crmCoverageKinds: () => ["admin", "platform", "crm", "coverage-kinds"] as const,
  crmFunnel: (since: string, until: string) => ["admin", "platform", "crm", "funnel", since, until] as const,
  // Per-custom-language FE translation bundles (namespace → key → value).
  platformLanguageBundles: (code: string) =>
    ["admin", "platform", "languages", code, "bundles"] as const,
  // Per-tenant UX flags for the discount surface (cart bulk-hint banner).
  discountSettings: () => ["admin", "discount-settings"] as const,
  inspectionSummaryPrice: () => ["admin", "inspection-summary-price"] as const,
  // Quote validity windows — two-layer config.
  platformQuoteSettings: () => ["admin", "platform-quote-settings"] as const,
  tenantQuoteSettings: () => ["admin", "tenant-quote-settings"] as const,
  // Tenant-side read of which admin pages/tabs are hidden because
  // the tenant's operation graph lacks a matching node. Distinct from
  // ``pagesVisibility`` above (which is per-tenant CUSTOMER-facing
  // page toggles).
  adminPageVisibility: () => ["admin", "page-visibility"] as const,
  // Platform-admin CRUD over the page→graph-node rules. Distinct
  // queryKey so it doesn't collide with the tenant read above.
  adminPageVisibilityRules: () =>
    ["admin", "platform", "page-visibility-rules"] as const,
  adminPageVisibilityKnownMarkers: () =>
    ["admin", "platform", "page-visibility-rules", "known-markers"] as const,
  adminPageVisibilityAppRules: () =>
    ["admin", "platform", "page-visibility-rules", "from-apps"] as const,
  adminPageVisibilityKnownEntitlements: () =>
    [
      "admin",
      "platform",
      "page-visibility-rules",
      "known-entitlements",
    ] as const,
  adminPageVisibilityKnownLimits: () =>
    ["admin", "platform", "page-visibility-rules", "known-limits"] as const,
} as const;
